import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Code, Copy, Trash2, ArrowLeftRight, Download } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const XmlJsonConverter: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [activeTab, setActiveTab] = useState<'xml-to-json' | 'json-to-xml'>('xml-to-json');
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState('');
  const [prettyPrint, setPrettyPrint] = useState(true);
  const [includeTextNodes, setIncludeTextNodes] = useState(true);
  const [arrayElements, setArrayElements] = useState<string>('');

  useSEO({
    title: 'XML to JSON Converter - Convert XML to JSON and JSON to XML Online',
    description: 'Free online XML to JSON converter. Convert XML documents to JSON format and JSON data to XML with customizable formatting options.',
    keywords: 'XML to JSON, JSON to XML, XML converter, JSON converter, XML JSON parser',
    canonical: 'https://snap-tools.online/dev/xml-json-converter'
  });

  // XML to JSON conversion
  const xmlToJson = (xmlStr: string): any => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlStr, 'text/xml');
    
    if (xmlDoc.querySelector('parsererror')) {
      throw new Error('Invalid XML format');
    }

    const convertNode = (node: Element | Document): any => {
      const obj: any = {};
      
      // Handle document node
      if (node.nodeType === Node.DOCUMENT_NODE) {
        const rootElement = (node as Document).documentElement;
        if (rootElement) {
          return { [rootElement.tagName]: convertNode(rootElement) };
        }
        return {};
      }

      const element = node as Element;
      
      // Handle attributes
      if (element.attributes && element.attributes.length > 0) {
        obj['@attributes'] = {};
        Array.from(element.attributes).forEach(attr => {
          obj['@attributes'][attr.name] = attr.value;
        });
      }

      // Handle child elements and text content
      const children = Array.from(element.childNodes);
      const textContent = children
        .filter(child => child.nodeType === Node.TEXT_NODE)
        .map(child => child.textContent?.trim())
        .filter(text => text)
        .join(' ');

      const elementChildren = children.filter(child => child.nodeType === Node.ELEMENT_NODE) as Element[];

      if (elementChildren.length === 0) {
        // Leaf node with text content
        if (textContent && includeTextNodes) {
          if (obj['@attributes']) {
            obj['#text'] = textContent;
          } else {
            return textContent;
          }
        }
        return Object.keys(obj).length > 0 ? obj : null;
      }

      // Group children by tag name
      const childGroups: { [key: string]: Element[] } = {};
      elementChildren.forEach(child => {
        const tagName = child.tagName;
        if (!childGroups[tagName]) {
          childGroups[tagName] = [];
        }
        childGroups[tagName].push(child);
      });

      // Convert child groups
      Object.entries(childGroups).forEach(([tagName, elements]) => {
        const arrayElementList = arrayElements.split(',').map(s => s.trim()).filter(s => s);
        const isArray = elements.length > 1 || arrayElementList.includes(tagName);
        
        if (isArray) {
          obj[tagName] = elements.map(el => convertNode(el));
        } else {
          obj[tagName] = convertNode(elements[0]);
        }
      });

      // Add text content if there are child elements
      if (textContent && includeTextNodes && elementChildren.length > 0) {
        obj['#text'] = textContent;
      }

      return obj;
    };

    return convertNode(xmlDoc);
  };

  // JSON to XML conversion
  const jsonToXml = (jsonStr: string): string => {
    const jsonObj = JSON.parse(jsonStr);

    const convertToXml = (obj: any, rootName?: string): string => {
      if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
        return rootName ? `<${rootName}>${obj}</${rootName}>` : String(obj);
      }

      if (Array.isArray(obj)) {
        return obj.map(item => convertToXml(item, rootName)).join('');
      }

      if (typeof obj === 'object' && obj !== null) {
        let xml = '';
        
        Object.entries(obj).forEach(([key, value]) => {
          if (key === '@attributes') {
            return; // Handle attributes separately
          }
          
          if (key === '#text') {
            xml += String(value);
            return;
          }

          const attributes = obj['@attributes'] && key !== '#text' ? obj['@attributes'] : {};
          const attrStr = Object.entries(attributes)
            .map(([attrKey, attrValue]) => `${attrKey}="${attrValue}"`)
            .join(' ');

          if (Array.isArray(value)) {
            xml += value.map(item => {
              const itemXml = convertToXml(item);
              return `<${key}${attrStr ? ' ' + attrStr : ''}>${itemXml}</${key}>`;
            }).join('');
          } else {
            const valueXml = convertToXml(value);
            xml += `<${key}${attrStr ? ' ' + attrStr : ''}>${valueXml}</${key}>`;
          }
        });

        return rootName ? `<${rootName}>${xml}</${rootName}>` : xml;
      }

      return '';
    };

    // Handle root object
    if (typeof jsonObj === 'object' && !Array.isArray(jsonObj)) {
      const rootKeys = Object.keys(jsonObj);
      if (rootKeys.length === 1) {
        const rootKey = rootKeys[0];
        return `<?xml version="1.0" encoding="UTF-8"?>\n${convertToXml(jsonObj[rootKey], rootKey)}`;
      }
    }

    return `<?xml version="1.0" encoding="UTF-8"?>\n<root>${convertToXml(jsonObj)}</root>`;
  };

  const handleConversion = (value: string) => {
    setInput(value);
    setError('');
    setIsValid(true);

    if (!value.trim()) {
      setOutput('');
      return;
    }

    try {
      if (activeTab === 'xml-to-json') {
        const result = xmlToJson(value);
        setOutput(prettyPrint ? JSON.stringify(result, null, 2) : JSON.stringify(result));
      } else {
        const result = jsonToXml(value);
        setOutput(result);
      }
    } catch (e) {
      setIsValid(false);
      setError(e instanceof Error ? e.message : 'Conversion failed');
      setOutput('');
    }
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output).then(() => {
      toast.success('Output copied to clipboard');
    });
  };

  const downloadOutput = () => {
    const extension = activeTab === 'xml-to-json' ? 'json' : 'xml';
    const filename = `converted.${extension}`;
    
    const blob = new Blob([output], { type: activeTab === 'xml-to-json' ? 'application/json' : 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  };

  const clearInputs = () => {
    setInput('');
    setOutput('');
    setError('');
    setIsValid(true);
    toast.success('All fields cleared');
  };

  const loadSampleData = () => {
    if (activeTab === 'xml-to-json') {
      const sampleXML = `<?xml version="1.0" encoding="UTF-8"?>
<bookstore>
  <book id="1" category="fiction">
    <title lang="en">The Great Gatsby</title>
    <author>F. Scott Fitzgerald</author>
    <year>1925</year>
    <price currency="USD">12.99</price>
  </book>
  <book id="2" category="science">
    <title lang="en">A Brief History of Time</title>
    <author>Stephen Hawking</author>
    <year>1988</year>
    <price currency="USD">15.99</price>
  </book>
</bookstore>`;
      setInput(sampleXML);
      handleConversion(sampleXML);
    } else {
      const sampleJSON = `{
  "bookstore": {
    "book": [
      {
        "@attributes": {
          "id": "1",
          "category": "fiction"
        },
        "title": {
          "@attributes": {
            "lang": "en"
          },
          "#text": "The Great Gatsby"
        },
        "author": "F. Scott Fitzgerald",
        "year": "1925",
        "price": {
          "@attributes": {
            "currency": "USD"
          },
          "#text": "12.99"
        }
      },
      {
        "@attributes": {
          "id": "2",
          "category": "science"
        },
        "title": {
          "@attributes": {
            "lang": "en"
          },
          "#text": "A Brief History of Time"
        },
        "author": "Stephen Hawking",
        "year": "1988",
        "price": {
          "@attributes": {
            "currency": "USD"
          },
          "#text": "15.99"
        }
      }
    ]
  }
}`;
      setInput(sampleJSON);
      handleConversion(sampleJSON);
    }
    toast.success('Sample data loaded');
  };

  const switchMode = () => {
    setActiveTab(activeTab === 'xml-to-json' ? 'json-to-xml' : 'xml-to-json');
    setInput('');
    setOutput('');
    setError('');
    setIsValid(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Code className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            XML ↔ JSON Converter
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Convert XML documents to JSON format and JSON data to XML with customizable formatting 
            and advanced parsing options for seamless data transformation.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        {/* Tab Navigation */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="bg-white rounded-lg p-1 shadow-lg">
            <button
              onClick={() => setActiveTab('xml-to-json')}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'xml-to-json'
                  ? 'bg-[#8438ff] text-white'
                  : 'text-gray-600 hover:text-[#8438ff]'
              }`}
            >
              XML → JSON
            </button>
            <button
              onClick={() => setActiveTab('json-to-xml')}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'json-to-xml'
                  ? 'bg-[#8438ff] text-white'
                  : 'text-gray-600 hover:text-[#8438ff]'
              }`}
            >
              JSON → XML
            </button>
          </div>
        </div>

        {/* Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8 mb-6"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4">Conversion Options</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={prettyPrint}
                onChange={(e) => setPrettyPrint(e.target.checked)}
                className="rounded border-gray-300 text-[#8438ff] focus:ring-[#8438ff]"
              />
              <span className="text-sm">Pretty Print Output</span>
            </label>

            {activeTab === 'xml-to-json' && (
              <>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={includeTextNodes}
                    onChange={(e) => setIncludeTextNodes(e.target.checked)}
                    className="rounded border-gray-300 text-[#8438ff] focus:ring-[#8438ff]"
                  />
                  <span className="text-sm">Include Text Nodes</span>
                </label>

                <div className="md:col-span-2 lg:col-span-1">
                  <label className="block text-sm text-gray-700 mb-1">
                    Force Array Elements (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={arrayElements}
                    onChange={(e) => setArrayElements(e.target.value)}
                    placeholder="book,item,element"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff]"
                  />
                </div>
              </>
            )}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Input Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Input {activeTab === 'xml-to-json' ? 'XML' : 'JSON'}
                </h3>
                <div className="flex space-x-1 sm:space-x-2">
                  <button
                    onClick={loadSampleData}
                    className="px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Sample
                  </button>
                  <button
                    onClick={switchMode}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                    title="Switch mode"
                  >
                    <ArrowLeftRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                  <button
                    onClick={clearInputs}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Clear all"
                  >
                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>

              <textarea
                value={input}
                onChange={(e) => handleConversion(e.target.value)}
                placeholder={`Paste your ${activeTab === 'xml-to-json' ? 'XML' : 'JSON'} here...`}
                className={`w-full h-80 sm:h-96 p-3 sm:p-4 border-2 rounded-lg sm:rounded-xl resize-none font-mono text-xs sm:text-sm transition-colors ${
                  isValid 
                    ? 'border-gray-200 focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff]' 
                    : 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                }`}
              />

              {error && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <Code className="h-4 w-4 text-red-500 mr-2" />
                    <span className="text-red-700 text-sm">{error}</span>
                  </div>
                </div>
              )}

              <div className="mt-3 text-xs text-gray-600">
                <p>Input length: {input.length} characters</p>
                <p>Format: {activeTab === 'xml-to-json' ? 'XML Document' : 'JSON Object'}</p>
              </div>
            </motion.div>
          </div>

          {/* Output Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Output {activeTab === 'xml-to-json' ? 'JSON' : 'XML'}
                </h3>
                <div className="flex space-x-1 sm:space-x-2">
                  {output && (
                    <>
                      <button
                        onClick={copyOutput}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                      <button
                        onClick={downloadOutput}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                        title="Download as file"
                      >
                        <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <textarea
                value={output}
                readOnly
                className="w-full h-80 sm:h-96 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none bg-gray-50 font-mono text-xs sm:text-sm"
                placeholder={`Converted ${activeTab === 'xml-to-json' ? 'JSON' : 'XML'} will appear here...`}
              />

              {output && (
                <div className="mt-3 text-xs text-gray-600">
                  <p>Output length: {output.length} characters</p>
                  <p>Lines: {output.split('\n').length}</p>
                </div>
              )}

              {/* Conversion Info */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  {activeTab === 'xml-to-json' ? 'XML to JSON' : 'JSON to XML'} Conversion
                </h4>
                <div className="text-xs text-blue-800 space-y-1">
                  {activeTab === 'xml-to-json' ? (
                    <>
                      <p>• Attributes are stored in @attributes objects</p>
                      <p>• Text content is stored in #text properties</p>
                      <p>• Multiple elements become arrays</p>
                      <p>• XML declarations and processing instructions are ignored</p>
                    </>
                  ) : (
                    <>
                      <p>• @attributes objects become XML attributes</p>
                      <p>• #text properties become element text content</p>
                      <p>• Arrays become multiple XML elements</p>
                      <p>• XML declaration is automatically added</p>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8"
        />
      </div>
    </div>
  );
};

export default XmlJsonConverter;
